import { useState, useEffect } from 'react';
import { useLoaderData, useNavigate, Form, useActionData } from 'react-router';
import type { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { 
  ArrowLeft, Save, Download, Play, AlertCircle, FileText, 
  Eye, Code, RefreshCw, Clock 
} from 'lucide-react';
import { requireUser } from '../../../lib/session.server';
import { resumeApi } from '../../../lib/api';
import { Card, Button, Badge } from '../../../components/ui';
import { ResumeStatusChip, useToastActions } from '../../../components/ui';
import type { Resume, ResumeText, RenderProgress, User } from '../../../types';

interface ResumeEditorData {
  user: User;
  resume: Resume;
  resumeText: ResumeText;
  renderProgress?: RenderProgress;
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const resumeId = params.id as string;

  try {
    const [resume, resumeText, renderProgress] = await Promise.allSettled([
      resumeApi.getResume(resumeId),
      resumeApi.getResumeText(resumeId),
      resumeApi.getRenderProgress(resumeId),
    ]);

    if (resume.status === 'rejected') {
      throw new Error('Resume not found');
    }

    return {
      user,
      resume: resume.value,
      resumeText: resumeText.status === 'fulfilled' ? resumeText.value : null,
      renderProgress: renderProgress.status === 'fulfilled' ? renderProgress.value : null,
    };
  } catch (error) {
    throw new Response('Resume not found', { status: 404 });
  }
}

export async function action({ request, params }: ActionFunctionArgs) {
  const user = await requireUser(request);
  const resumeId = params.id as string;
  const formData = await request.formData();
  const intent = formData.get('intent') as string;

  try {
    // Verify user owns this resume before allowing actions
    const resume = await resumeApi.getResume(resumeId);
    if (resume.user_id !== user.id) {
      return { success: false, error: 'Unauthorized access to resume' };
    }

    switch (intent) {
      case 'save': {
        const content = formData.get('content') as string;
        await resumeApi.updateResumeText(resumeId, content);
        return { success: true, message: 'Resume saved successfully' };
      }
      
      case 'render': {
        const jobResponse = await resumeApi.renderResume(resumeId);
        return { success: true, message: 'PDF rendering started', jobId: jobResponse.job_id };
      }
      
      default:
        return { success: false, error: 'Invalid action' };
    }
  } catch (error) {
    return { success: false, error: 'Action failed' };
  }
}

export function ResumeEditorPage() {
  const { resume, resumeText: initialResumeText, renderProgress: initialRenderProgress } = useLoaderData() as ResumeEditorData;
  const actionData = useActionData() as { success?: boolean; error?: string; message?: string };
  const navigate = useNavigate();
  const { success, error } = useToastActions();

  const [content, setContent] = useState(initialResumeText?.content || '');
  const [isDirty, setIsDirty] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [renderProgress, setRenderProgress] = useState<RenderProgress | null>(initialRenderProgress || null);
  const [wordCount, setWordCount] = useState(0);

  // Handle action data (form submission result)
  useEffect(() => {
    if (actionData?.success) {
      success('Action Successful', actionData.message || 'Operation completed.');
      if (actionData.message?.includes('saved')) {
        setIsDirty(false);
      }
    } else if (actionData?.error) {
      error('Action Failed', actionData.error);
    }
  }, [actionData, success, error]);

  // Track content changes
  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    setWordCount(words);
    
    if (initialResumeText) {
      setIsDirty(content !== initialResumeText.content);
    }
  }, [content, initialResumeText]);

  // Poll render progress if active
  useEffect(() => {
    if (renderProgress && renderProgress.status === 'processing') {
      const interval = setInterval(async () => {
        try {
          const progress = await resumeApi.getRenderProgress(resume.id);
          if (progress) {
            setRenderProgress(progress);
            if (progress.status === 'completed' || progress.status === 'failed') {
              clearInterval(interval);
            }
          }
        } catch {
          // Progress polling failed, stop polling
          clearInterval(interval);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [renderProgress, resume.id]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('intent', 'save');
      formData.append('content', content);
      
      const response = await fetch('', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        success('Resume Saved', 'Your changes have been saved.');
        setIsDirty(false);
      } else {
        throw new Error('Save failed');
      }
    } catch (err) {
      error('Save Failed', 'Unable to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRender = async () => {
    try {
      const formData = new FormData();
      formData.append('intent', 'render');
      
      const response = await fetch('', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        success('PDF Rendering Started', 'Your resume PDF is being generated.');
        // Start polling for progress
        setRenderProgress({
          resume_id: resume.id,
          job_id: 'pending',
          status: 'processing',
          progress_percent: 0,
          stage: 'Starting',
        });
      } else {
        throw new Error('Render failed');
      }
    } catch (err) {
      error('Render Failed', 'Unable to start PDF rendering.');
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await resumeApi.downloadGenerated(resume.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resume-generated-${resume.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      error('Download Failed', 'Generated PDF not available or download failed.');
    }
  };

  // Show loading state if resume text not available
  if (!initialResumeText) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/resumes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Resume Editor</h1>
        </div>

        <Card>
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">Resume Not Ready</h3>
            <p className="text-muted-foreground mb-4">
              This resume hasn't been parsed yet. Please wait for processing to complete.
            </p>
            <div className="flex justify-center gap-2">
              <ResumeStatusChip
                status={resume.status?.parsed ? 'completed' : resume.status?.parse_status || 'pending'}
                type="parse"
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/resumes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Resume Editor</h1>
        </div>
        <p className="text-muted-foreground ml-11">
          Edit your resume content in markdown format and generate PDFs.
        </p>
      </div>

      {/* Status Bar */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h3 className="font-semibold text-foreground">{resume.original_filename}</h3>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex gap-2">
                  <ResumeStatusChip
                    status={resume.status?.parsed ? 'completed' : resume.status?.parse_status || 'pending'}
                    type="parse"
                  />
                  <ResumeStatusChip
                    status={resume.status?.tagged ? 'completed' : resume.status?.tag_status || 'pending'}
                    type="tag"
                  />
                  <ResumeStatusChip
                    status={resume.status?.rendered ? 'completed' : resume.status?.render_status || 'pending'}
                    type="render"
                  />
                </div>
                <span className="text-sm text-muted-foreground">
                  {wordCount} words • Version {initialResumeText.version}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPreview(!isPreview)}
              className="gap-1"
            >
              {isPreview ? <Code className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {isPreview ? 'Edit' : 'Preview'}
            </Button>

            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="gap-1"
            >
              {isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>

            {resume.status?.rendered ? (
              <Button variant="secondary" onClick={handleDownload} className="gap-1">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={handleRender}
                disabled={!resume.status?.parsed}
                className="gap-1"
              >
                <Play className="h-4 w-4" />
                Render PDF
              </Button>
            )}
          </div>
        </div>

        {/* Render Progress */}
        {renderProgress && renderProgress.status === 'processing' && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">
                    Rendering PDF: {renderProgress.stage}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {renderProgress.progress_percent}%
                  </span>
                </div>
                <div className="w-full bg-muted/30 rounded-full h-2">
                  <div 
                    className="bg-primary rounded-full h-2 transition-all duration-300"
                    style={{ width: `${renderProgress.progress_percent}%` }}
                  />
                </div>
                {renderProgress.estimated_completion && (
                  <div className="text-xs text-muted-foreground mt-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    ETA: {new Date(renderProgress.estimated_completion).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Render Error */}
        {renderProgress && renderProgress.status === 'failed' && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive mb-1">PDF Rendering Failed</p>
                  <p className="text-destructive/80">
                    {renderProgress.error || 'An error occurred during PDF generation. Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Card>
            {isPreview ? (
              <div className="prose prose-sm prose-invert max-w-none">
                <div 
                  className="whitespace-pre-wrap text-foreground"
                  dangerouslySetInnerHTML={{ 
                    __html: content
                      .replace(/\n/g, '<br>')
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  }}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">Resume Content</h3>
                  {isDirty && (
                    <Badge variant="secondary" className="gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Unsaved changes
                    </Badge>
                  )}
                </div>
                
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full h-96 px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 font-mono text-sm resize-none"
                  placeholder="Enter your resume content in markdown format..."
                />

                <div className="text-xs text-muted-foreground bg-muted/30 rounded-xl p-3">
                  <p>
                    <strong>Markdown Support:</strong> Use **bold**, *italic*, and line breaks for formatting. 
                    The content will be rendered as a clean PDF when you generate it.
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-4">
          {/* Quick Actions */}
          <Card>
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Quick Actions</h3>
              
              <div className="space-y-2">
                <Form method="post">
                  <input type="hidden" name="intent" value="save" />
                  <input type="hidden" name="content" value={content} />
                  <Button
                    type="submit"
                    disabled={!isDirty || isSaving}
                    className="w-full gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Form>

                <Form method="post">
                  <input type="hidden" name="intent" value="render" />
                  <Button
                    type="submit"
                    variant="secondary"
                    disabled={!resume.status?.parsed || isDirty}
                    className="w-full gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Generate PDF
                  </Button>
                </Form>

                {resume.status?.rendered && (
                  <Button
                    variant="secondary"
                    onClick={handleDownload}
                    className="w-full gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                )}
              </div>

              {isDirty && (
                <div className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                  <p>Save your changes before generating a PDF to ensure the latest content is included.</p>
                </div>
              )}
            </div>
          </Card>

          {/* Statistics */}
          <Card>
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Document Stats</h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Words</span>
                  <span className="text-sm font-medium text-foreground">{wordCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Characters</span>
                  <span className="text-sm font-medium text-foreground">{content.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Lines</span>
                  <span className="text-sm font-medium text-foreground">{content.split('\n').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Version</span>
                  <span className="text-sm font-medium text-foreground">{initialResumeText.version}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Tips */}
          <Card>
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Formatting Tips</h3>
              
              <div className="space-y-3 text-sm text-muted-foreground">
                <div>
                  <code className="bg-muted/50 px-1 py-0.5 rounded">**Bold Text**</code>
                  <p>Use double asterisks for bold</p>
                </div>
                <div>
                  <code className="bg-muted/50 px-1 py-0.5 rounded">*Italic Text*</code>
                  <p>Use single asterisks for italic</p>
                </div>
                <div>
                  <code className="bg-muted/50 px-1 py-0.5 rounded"># Heading</code>
                  <p>Use # for section headings</p>
                </div>
                <div>
                  <code className="bg-muted/50 px-1 py-0.5 rounded">- List item</code>
                  <p>Use - for bullet points</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}